import { useCallback, useEffect, useRef, useState } from "react";

import AppShell from "../components/AppShell";
import {
  activateInstallerRelease,
  deleteInstallerRelease,
  getInstallerInfo,
  listInstallerReleases,
  uploadInstaller,
  type InstallerInfo,
  type InstallerRelease,
} from "../api/agents";
import { useAuth } from "../stores/auth";

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function InstallerAdmin() {
  const tenant = useAuth((s) => s.tenant);
  const [current, setCurrent] = useState<InstallerInfo | null>(null);
  const [history, setHistory] = useState<InstallerRelease[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [version, setVersion] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    try {
      const [info, releases] = await Promise.all([
        getInstallerInfo(),
        listInstallerReleases(),
      ]);
      setCurrent(info);
      setHistory(releases);
      setLoadError(null);
    } catch {
      setLoadError("Could not load installer history.");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!tenant?.billing_exempt) {
    return (
      <AppShell>
        <div className="mx-auto max-w-lg py-16 text-center">
          <p className="text-slate-600">You don't have access to this page.</p>
        </div>
      </AppShell>
    );
  }

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !version.trim()) return;
    setUploading(true);
    setUploadError(null);
    setSuccess(false);
    try {
      await uploadInstaller(file, version.trim());
      setSuccess(true);
      setVersion("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setUploadError(e?.response?.data?.detail ?? "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  async function handleActivate(release: InstallerRelease) {
    const isDowngrade =
      history && current?.version && release.version < current.version;
    const msg = isDowngrade
      ? `Roll back to ${release.version}? Agents currently on a newer version won't downgrade automatically, but new heartbeats from older agents will receive this binary.`
      : `Make ${release.version} the active installer? Agents will receive it on their next check-in.`;
    if (!window.confirm(msg)) return;
    setBusyId(release.id);
    setActionError(null);
    try {
      await activateInstallerRelease(release.id);
      await refresh();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e?.response?.data?.detail ?? "Could not activate this release.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(release: InstallerRelease) {
    if (
      !window.confirm(
        `Delete release ${release.version}? This permanently removes the binary from the server.`,
      )
    ) {
      return;
    }
    setBusyId(release.id);
    setActionError(null);
    try {
      await deleteInstallerRelease(release.id);
      await refresh();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setActionError(e?.response?.data?.detail ?? "Could not delete this release.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl">
        <h1 className="text-2xl font-semibold text-slate-900">Installer Upload</h1>
        <p className="mt-1 text-slate-600">
          Upload a new agent .exe. Agents will download it automatically on their next heartbeat.
        </p>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-medium text-slate-500">Current active version</h2>
          {loadError ? (
            <p className="mt-1 text-sm text-red-600">{loadError}</p>
          ) : current ? (
            <p className="mt-1 font-mono text-base font-semibold text-slate-900">
              {current.version ?? "—"}
            </p>
          ) : (
            <p className="mt-1 text-sm text-slate-400">Loading…</p>
          )}
        </div>

        <form
          onSubmit={handleUpload}
          className="mt-6 rounded-2xl border border-slate-200 bg-white p-5"
        >
          <h2 className="mb-4 text-base font-semibold text-slate-900">Upload new version</h2>

          <label className="block">
            <span className="block text-sm font-medium text-slate-700">Version</span>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g. 0.8.0"
              maxLength={20}
              required
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
            />
          </label>

          <label className="mt-4 block">
            <span className="block text-sm font-medium text-slate-700">Installer file (.exe)</span>
            <input
              ref={fileRef}
              type="file"
              accept=".exe"
              required
              aria-label="Installer .exe file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700"
            />
          </label>

          {file && (
            <p className="mt-2 text-xs text-slate-500">
              {file.name} &mdash; {formatSize(file.size)}
            </p>
          )}

          {uploadError && (
            <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {uploadError}
            </p>
          )}

          {success && (
            <p role="status" className="mt-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
              Upload successful. Agents will update on their next check-in.
            </p>
          )}

          <button
            type="submit"
            disabled={uploading || !file || !version.trim()}
            className="no-tap-highlight mt-4 w-full rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {uploading ? "Uploading…" : "Upload installer"}
          </button>
        </form>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="mb-3 text-base font-semibold text-slate-900">Release history</h2>
          {actionError && (
            <p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {actionError}
            </p>
          )}
          {history === null ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-slate-500">No releases uploaded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-2 pr-3">Version</th>
                    <th className="py-2 pr-3">Size</th>
                    <th className="py-2 pr-3">Uploaded</th>
                    <th className="py-2 pr-3">By</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map((r) => (
                    <tr key={r.id}>
                      <td className="py-2 pr-3 font-mono">
                        {r.version}
                        {r.is_security_release && (
                          <span
                            className="ml-2 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-800"
                            title="Marked as a security release"
                          >
                            Security
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-slate-600">{formatSize(r.size_bytes)}</td>
                      <td className="py-2 pr-3 text-slate-600">{formatDate(r.uploaded_at)}</td>
                      <td className="py-2 pr-3 text-slate-600">
                        {r.uploaded_via === "ci" ? (
                          <span className="text-slate-500">CI</span>
                        ) : (
                          r.uploaded_by_email ?? "—"
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        {r.is_active ? (
                          <span className="inline-block rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">Inactive</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <div className="inline-flex gap-2">
                          {!r.is_active && (
                            <button
                              type="button"
                              onClick={() => handleActivate(r)}
                              disabled={busyId !== null}
                              className="no-tap-highlight rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                              {busyId === r.id ? "…" : "Activate"}
                            </button>
                          )}
                          {!r.is_active && (
                            <button
                              type="button"
                              onClick={() => handleDelete(r)}
                              disabled={busyId !== null}
                              className="no-tap-highlight rounded-md border border-red-200 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                            >
                              {busyId === r.id ? "…" : "Delete"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
